import React, { FC, useEffect, useState } from 'react';
import Heading from '~ComponentsImproved/Typography/Heading';
import { Printer } from '~reducers';
import { BaseInputStyling, BlueButton, Box, Button } from '~Styles';
import TrashIcon from '~Assets/icons/delete.svg';
import Icon from '~ComponentsImproved/Icon';
import { useButton, useDispatch, useI18n, useLogger, usePrinterApi } from '~Hooks';
import { GetPrinters_venue_printers, GetStations_user_company_stations } from '../__queries__';
import * as actions from '~actions';
import Modal from '~ComponentsImproved/Modal';
import { ModalContent } from '~Styles/modal';
import styled from '@emotion/styled';
import SelectListMulti from '~ComponentsImproved/SelectListMulti';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import PrinterTroubleshooting from '../Troubleshooting/AddedPrinter';
import { PRINTERS_WITHOUT_STATUS } from '~Components/PrinterManager';
import { handyBreakpoint } from '~Styles/breakpoints';

const Wrapper = styled.div`
  background-color: #fff;
  border-radius: 4px;
  box-sizing: border-box;
  color: #000;
  overflow: hidden;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 4px 16px;
  height: 56px;
  box-sizing: border-box;
  color: #000;
  align-items: center;
  background-color: var(--colorBlueGray300);

  button {
    white-space: nowrap;
    height: 40px;
  }
`;

const LeftTopSection = styled.div`
  margin-right: auto;
  display: flex;
  gap: 8px;
  align-items: center;

  h4 {
    word-break: break-word;
    padding-right: 8px;
  }

  ${handyBreakpoint} {
    flex-direction: column;
    gap: 2px;
    align-items: flex-start;
    justify-content: center;
  }
`;

const Tag = styled.div`
  padding: 4px 8px;
  background-color: var(--colorUIGreen);
  color: #fff;
  border-radius: 4px;
  width: auto;
  font-weight: 600;
  font-size: 14px;

  ${handyBreakpoint} {
    font-size: 12px;
    padding: 2px 4px;
  }
`;

const Content = styled.div`
  padding: 16px;
  box-sizing: border-box;
  position: relative;
`;

const Buttons = styled.div`
  display: flex;
  gap: 16px;

  ${handyBreakpoint} {
    flex-wrap: wrap;

    & > button {
      flex: 1;
      height: 40px;
      min-height: 40px;
    }
  }
`;

const PrintButton = styled(BlueButton)`
  min-height: 32px;
  padding: 12px 16px;
`;

const RemoveButton = styled(Button)`
  background-color: var(--colorUIRed);
  margin-left: auto;
  width: 40px;
  min-width: 40px;
  padding: 4px;
  height: 40px;
  min-height: 40px;
  align-self: flex-end;
  flex: 0 !important;
`;

const NameInput = styled.input`
  ${BaseInputStyling}
  padding: 8px 16px;
`;

const PrinterStatusWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;

  ${handyBreakpoint} {
    display: none;
  }
`;

const Info = styled.div`
  display: flex;

  p {
    margin: 0;
    flex: 1;
  }
`;

const PrinterInfo = styled.div`
  margin-bottom: 16px;

  label {
    width: 90px;
    display: inline-block;
  }
`;

export const HeadingWrapper = styled.div`
  h2 {
    ${handyBreakpoint} {
      font-size: 16px;
    }
  }
`;

interface CloudPrinterInfo {
  id: string;
  status: string | null;
  statusCode: string | null;
  lastSeen: string;
  lastPrinted: string | null;
}

type CloudPrinterHealth = 'online' | 'offline' | 'warning';

/** Determines the health state of a cloud printer */
function getCloudPrinterHealth(cp: CloudPrinterInfo): { health: CloudPrinterHealth; label: string } {
  const lastSeenMs = Date.now() - new Date(cp.lastSeen).getTime();
  const lastSeenStr = formatLastSeen(cp.lastSeen);

  // Printer hasn't polled recently — it's offline
  if (lastSeenMs > 30 * 1000) {
    return { health: 'offline', label: `Offline (${lastSeenStr})` };
  }

  // Printer is online but reporting a hardware issue
  if (cp.status === 'cover_open' || (cp.statusCode && !cp.statusCode.startsWith('2'))) {
    const issue = cp.statusCode || 'Hardware issue';
    return { health: 'warning', label: issue };
  }

  return { health: 'online', label: `Online (${lastSeenStr})` };
}

/** Returns a human-readable "last seen" string */
function formatLastSeen(lastSeen: string): string {
  const diff = Date.now() - new Date(lastSeen).getTime();
  if (diff < 10 * 1000) return 'just now';
  if (diff < 60 * 1000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)}m ago`;
  return `${Math.floor(diff / 3600000)}h ago`;
}

const HEALTH_COLORS: Record<CloudPrinterHealth, string> = {
  online: 'var(--colorUIGreen)',
  offline: 'var(--colorUIRed)',
  warning: 'var(--colorUIYellow, #f59e0b)',
};

const CloudStatusTag = styled.div<{ health: CloudPrinterHealth }>`
  padding: 4px 8px;
  background-color: ${({ health }) => HEALTH_COLORS[health]};
  color: #fff;
  border-radius: 4px;
  width: auto;
  font-weight: 600;
  font-size: 12px;

  ${handyBreakpoint} {
    font-size: 10px;
    padding: 2px 4px;
  }
`;

interface Props {
  printer: Printer;
  stations: GetStations_user_company_stations[];
  venuePrinter?: GetPrinters_venue_printers;
  updateVenuePrinter: (id: string, name: string, stations: string[], type: string) => void;
  cloudPrinter?: CloudPrinterInfo;
}

const AddedPrinter: FC<Props> = ({ printer, stations, venuePrinter, updateVenuePrinter, cloudPrinter }) => {
  const logger = useLogger('settings-v2');
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const printerApi = usePrinterApi();
  const [showPrinterNameInput, setShowPrinterNameInput] = useState(false);
  const [showStationsModal, setShowStationsModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [disableTestPrint, setDisableTestPrint] = useState(false);
  const [selectedStations, setSelectedStations] = useState<string[]>(printer.stations);
  const [printerDebugsessionId, setPrinterDebugSessionId] = useState<string>();

  const getStationName = (stationId: string) => {
    return stations.find((station) => station.id === stationId)?.name ?? stationId;
  };

  useEffect(() => {
    setPrinterDebugSessionId(
      Math.random()
        .toString(36)
        .substring(2, 15),
    );
  }, []);

  const TestPrintString = `
          
===== TEST =====


${new Date().toLocaleTimeString()}

${printer.id} ${venuePrinter ? venuePrinter.name : ''} 

${printer.stations
  .map((id) => stations?.find((st) => st.id === id)?.name ?? i18n('Settings.UnknownStation'))
  .join(', ') ?? i18n('Settings.NoStations')}
  
  `;

  const testPrint = async () => {
    if (disableTestPrint) {
      return;
    }

    const printerName = printerApi.getNameFromId(printer.id);

    const loggerPrinterData = {
      printerId: printer.id,
      printerName: printerName,
      model: printer.model,
      createdAt: printer.createdAt,
      lastPrinted: printer.lastPrinted,
      printerDebugsessionId,
    };

    setDisableTestPrint(true);

    logger.info('Sending test print. ID:' + printer.id + ' Name: ' + printerName, {
      loggerPrinterData,
    });

    const printerRequiresNetwork =
      !printer.id.toLowerCase().includes('bluetooth') && !printer.id.toLowerCase().includes('usb');

    // Only check internet connection if the printer requires network (Lan and Cloud)
    if (printerRequiresNetwork) {
      try {
        const reponse = await fetch('https://www.google.com', { mode: 'no-cors' });

        if (!reponse) {
          dispatch(actions.addToast(i18n('Settings.Printers.Toast.NoInternet'), true));
          return; // We return and do not try to get the printer status if there is no internet
        }
      } catch (error) {
        return; // We return and do not try to get the printer status if there is no internet
      }
    }

    dispatch(actions.addToast(i18n('Settings.Printers.Toast.Test'), true));

    // The get printer status is not working well with the MCP31LB and MCP21LB (old star lib in order-manager-app).
    // StarCloudPrinter and virtual does not support status check
    if (
      PRINTERS_WITHOUT_STATUS.includes(printer.model) ||
      printer.model.toLowerCase().startsWith('tm') // TODO: fix status for epson printers
    ) {
      logger.info('Skipping getting status because of printer model: ' + printer.model, {
        loggerPrinterData,
      });

      window.printerManager?.initiatePrint({
        printerId: printer.id,
        data: TestPrintString,
        printType: 'TEST_PRINT',
      });

      // To avoid spamming the test print button, add time to let it print before enabling the button again
      setTimeout(() => {
        setDisableTestPrint(false);
      }, 2000);

      return;
    }

    // Send the test print after getting the printer status
    window.printerManager?.initiatePrint({
      printerId: printer.id,
      data: TestPrintString,
      printType: 'TEST_PRINT',
    });

    // To avoid spamming the test print button, add time to let it print before enabling the button again
    setTimeout(() => {
      setDisableTestPrint(false);
    }, 2000);
  };

  function setPrimary() {
    logger.info('Updating Settings (set primary printer)', {
      printerId: printer.id,
    });
    window.postMessageBridge({
      setPrimary: {
        id: printer.id,
      },
    });
  }

  const renamePrinter = () => {
    setShowPrinterNameInput(false);

    if (venuePrinter) {
      logger.info('Updating Settings (rename printer)', {
        fields: { printerId: printer.id, newName },
      });
      updateVenuePrinter(venuePrinter.id, newName, venuePrinter.stations, venuePrinter.type);
    } else {
      dispatch(actions.addToast(i18n('Settings.Printers.Toast.NotSaved'), true));
    }
  };

  function removeStation(printerId: string, stationId: string, allStations: string[]) {
    window.postMessageBridge({
      removeStation: {
        printerId,
        stationId,
      },
    });

    const station = stations?.find((s) => s.id === stationId);
    logger.info('Updating Settings (remove printer station)', {
      fields: { printerId, stationId, name: station?.name ?? 'Unknown' },
    });
  }

  function addStation(printerId: string, stationId: string, allStations: string[]) {
    const station = stations?.find((s) => s.id === stationId);

    window.postMessageBridge({
      addStation: {
        printerId,
        stationId,
      },
    });

    logger.info('Updating Settings (add printer station)', {
      fields: { printerId, stationId, name: station?.name ?? 'Unknown' },
    });
  }

  const updateStations = () => {
    const stationsToAdd = selectedStations.filter((id) => !printer.stations.includes(id));
    const stationsToRemove = printer.stations.filter((id) => !selectedStations.includes(id));

    stationsToAdd.forEach((stationId) => addStation(printer.id, stationId, selectedStations));
    stationsToRemove.forEach((stationId) =>
      removeStation(printer.id, stationId, selectedStations),
    );

    if (venuePrinter) {
      updateVenuePrinter(venuePrinter.id, venuePrinter.name, selectedStations, venuePrinter.type);
    }

    setShowStationsModal(false);
  };

  const onStationSelect = (stationId: string) => {
    if (selectedStations.includes(stationId)) {
      setSelectedStations(selectedStations.filter((id) => id !== stationId));
    } else {
      setSelectedStations([...selectedStations, stationId]);
    }
  };

  const removePrinter = () => {
    logger.info('Updating Settings (remove printer)', {
      fields: { printerId: printer.id },
    });

    window.postMessageBridge({
      removePrinter: printer.id,
    });
  };

  const renameButton = useButton({
    onClick: () => setShowPrinterNameInput(true),
  });

  const assignStationsButton = useButton({
    onClick: () => setShowStationsModal(true),
  });

  const makePrimaryButton = useButton({
    onClick: setPrimary,
  });

  const removeButton = useButton({
    onClick: removePrinter,
  });

  const testPrintButton = useButton({
    onClick: testPrint,
  });

  const submitStationsButton = useButton({
    onClick: updateStations,
  });

  const renamePrinterButton = useButton({
    onClick: renamePrinter,
    disabled: !newName,
  });

  // The get printer status is not working well with the MCP31LB and MCP21LB (old star lib in order-manager-app). We don't know if these are connected via bluetooth or USB
  // StarCloudPrinter does not have a guide for troubleshooting yet
  const disallowTroubleShooting = PRINTERS_WITHOUT_STATUS.includes(printer.model);

  return (
    <>
      <Wrapper>
        <TopBar>
          <LeftTopSection>
            <Heading level={4}>{printerApi.getNameFromId(printer.id) ?? printer.id}</Heading>
            {printer.primary && <Tag>{i18n('Settings.Printers.Primary')}</Tag>}
            {cloudPrinter && (() => {
              const { health, label } = getCloudPrinterHealth(cloudPrinter);
              return <CloudStatusTag health={health}>{label}</CloudStatusTag>;
            })()}
          </LeftTopSection>
          <PrintButton {...testPrintButton} disabled={disableTestPrint}>
            {i18n('Settings.TestPrint')}
          </PrintButton>
        </TopBar>
        <Content>
          <PrinterInfo>
            <Info>
              <label>{i18n('Settings.Printers.Address')}:</label> {printer.id}
            </Info>
            <Info>
              <label>{i18n('Settings.Printers.Model')}:</label> {printer.model}
            </Info>
            {stations.length > 0 && (
              <Info>
                <label>{i18n('Settings.Printers.Stations')}: </label>{' '}
                <p>
                  {stations.length > 0 && printer.stations.length === 0 && (
                    <span style={{ color: 'var(--colorUIRed)' }}>
                      {i18n('Settings.Printers.Stations.None')}
                    </span>
                  )}
                  {printer.stations.length === stations.length
                    ? i18n('Settings.Printers.Stations.All')
                    : printer.stations.map((id) => getStationName(id)).join(', ')}
                </p>
              </Info>
            )}
          </PrinterInfo>

          <PrinterStatusWrapper>
            {!disallowTroubleShooting && (
              <PrinterTroubleshooting
                printer={printer}
                testPrint={testPrint}
                assignStation={() => setShowStationsModal(true)}
              />
            )}
          </PrinterStatusWrapper>

          <Buttons>
            <Button {...assignStationsButton}>{i18n('Settings.AssignStation')}</Button>
            {venuePrinter && <Button {...renameButton}>{i18n('Settings.Rename')}</Button>}
            {!printer.primary && (
              <Button {...makePrimaryButton}>{i18n('Settings.MakePrimary.Short')}</Button>
            )}
            <RemoveButton {...removeButton}>
              <Icon src={TrashIcon} color="#fff" height="24px" width="24px" />
            </RemoveButton>
          </Buttons>
        </Content>
      </Wrapper>

      {/* Printer name modal */}
      <Modal
        isShowing={showPrinterNameInput}
        hide={() => setShowPrinterNameInput(false)}
        position="flex-start">
        <ModalContent>
          <HeadingWrapper>
            <Heading level={2} color="#fff" bottom={16}>
              {i18n('Settings.Rename.Title')}
            </Heading>
          </HeadingWrapper>
          <Box marginBottom="medium">
            <NameInput
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
            />
          </Box>
          <BlueButton {...renamePrinterButton}>{i18n('General.Submit')}</BlueButton>
        </ModalContent>
      </Modal>

      {/* Stations select */}
      <Modal isShowing={showStationsModal} hide={() => setShowStationsModal(false)}>
        <ModalContent>
          <Heading level={2} color="#fff" bottom={24}>
            {i18n('Settings.Printers.Stations.Title')}
          </Heading>
          {stations.length === 0 ? (
            <Paragraph level={4} color="#fff">
              {i18n('Settings.Printers.NoStationsExist')}
            </Paragraph>
          ) : (
            <>
              <Box marginBottom="medium">
                <SelectListMulti
                  dark
                  list={stations.map((station) => ({
                    id: station.id,
                    name: station.name,
                  }))}
                  selectedIds={selectedStations}
                  onSelect={onStationSelect}
                  grid
                />
              </Box>
              <BlueButton {...submitStationsButton}>{i18n('General.Submit')}</BlueButton>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddedPrinter;
